import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON} from "../web/validation";
import type {ActivityPeriodStatus} from "./activity-period-status";
import {toActivityPeriodStatus} from "./activity-period-status";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";

export const ActivityPeriodReportValidator = t.intersection([
  t.type({
    id: t.string,
    activityPeriodId: t.string,
    activityPeriodStatus: t.string,
    rateModelId: t.string,
    url: t.string,
    billableLoads: t.number,
    billableTime: t.number,
    billableTimeUom: t.string,
    billableQty: t.number,
    billableQtyUom: t.string,
    totalCost: t.number,
    totalCostUom: t.string,
    totalLoads: t.number,
    totalTime: t.number,
    totalTimeUom: t.string,
    totalQty: t.number,
    totalQtyUom: t.string,
    lastModifiedDttm: t.number,
    createDttm: t.number,
  }),
  t.partial({
    companyId: t.string,
    companyName: t.string,
    companyAbbr: t.string,
    companyType: t.string,
    reportTruckCompanyId: t.string,
    reportTruckCompanyName: t.string,
    rateModelName: t.string,
    rateModelType: t.string,
    activityPeriodDttm: t.number,
    database: t.string,
  }),
]);

export const ActivityPeriodReportArrayValidator = t.array(
  ActivityPeriodReportValidator
);

export type ActivityPeriodReport = t.TypeOf<
  typeof ActivityPeriodReportValidator
>;
export type ActivityPeriodReportArray = t.TypeOf<
  typeof ActivityPeriodReportArrayValidator
>;

@Exclude()
export class ActivityPeriodReportEntity {
  @Expose() id!: string;
  @Expose() activityPeriodId!: string;

  @Transform(getDatabaseCodeTransformer(toActivityPeriodStatus))
  @Expose()
  activityPeriodStatus!: ActivityPeriodStatus;

  @Expose() rateModelId!: string;
  @Expose() name!: string;
  @Expose() url!: string;
  @Expose() billableLoads!: number;
  @Expose() billableTime!: number;
  @Expose() billableTimeUom!: string;
  @Expose() billableQty!: number;
  @Expose() billableQtyUom!: string;
  @Expose() totalCost!: number;
  @Expose() totalCostUom!: string;
  @Expose() totalLoads!: number;
  @Expose() totalTime!: number;
  @Expose() totalTimeUom!: string;
  @Expose() totalQty!: number;
  @Expose() totalQtyUom!: string;
  @Expose() createDttm!: number;
  @Expose() lastModifiedDttm!: number;

  // extras
  @Expose() companyId?: string;
  @Expose() companyName?: string;
  @Expose() companyAbbr?: string;
  @Expose() companyType?: string;
  @Expose() reportTruckCompanyId?: string;
  @Expose() reportTruckCompanyName?: string;
  @Expose() rateModelName?: string;
  @Expose() rateModelType?: string;
  @Expose() activityPeriodDttm?: number;
  @Expose() database?: string;

  /**
   * Constructor for ActivityPeriodReportEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a ActivityPeriodReportEntity from a type value.
   *
   * @param activityPeriodReport the type value.
   */
  static from(
    activityPeriodReport: ActivityPeriodReport
  ): ActivityPeriodReportEntity {
    return plainToInstance(
      ActivityPeriodReportEntity,
      activityPeriodReport,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  /**
   * Deserializes the ActivityPeriodReportEntity to a ActivityPeriodReport type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<ActivityPeriodReport> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ActivityPeriodReportValidator, object);
  }
}
